import { diff as deepObjectDiff } from 'deep-object-diff';
import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import omit from 'lodash/omit';

import {
  IAddress,
  IAutoCoverageVariables,
  IAutoPolicy,
  IAutoPolicyDetails,
  IAutoPolicyHolder,
  IVehicleVariables,
  IPolicyDriver,
  IPolicyVehicle,
} from '../../../interfaces';

type PartialVehicle = {
  variables: Partial<IVehicleVariables>;
} & Partial<Omit<IPolicyVehicle, 'variables'>>;

export type TDiffAutoPolicyDetails = {
  drivers: Partial<IPolicyDriver>[];
  vehicles: PartialVehicle[];
  variables: Partial<IAutoCoverageVariables>;
};

export type TDiffPolicy = {
  id?: string;
  status?: string;
  rating_address?: Partial<IAddress>;
  details?: TDiffAutoPolicyDetails;
  payment_method?: {
    id: string;
  };
  policy_holders?: IAutoPolicyHolder[];
};

function diff(
  obj1: Partial<IAutoPolicy> | IAutoPolicyDetails,
  obj2: Partial<IAutoPolicy> | IAutoPolicyDetails
) {
  return deepObjectDiff(obj1, obj2);
}

export function diffPolicy(_oldPolicy: IAutoPolicy, _newPolicy: IAutoPolicy) {
  /**
   * Validations
   * =========================
   */

  if (!isPlainObject(_oldPolicy) || isEmpty(_oldPolicy)) {
    return;
  }

  if (!isPlainObject(_newPolicy) || isEmpty(_newPolicy)) {
    return cloneDeep(_oldPolicy);
  }

  if (_oldPolicy.id !== _newPolicy.id) {
    throw new Error('Policies do not match');
  }

  /**
   * Mutations
   * =========================
   */

  const oldPolicy = cloneDeep(_oldPolicy);
  const newPolicy = cloneDeep(_newPolicy);

  const clonedNewPolicy = cloneDeep(newPolicy);
  const policyDiff: TDiffPolicy = {
    id: clonedNewPolicy.id,
  };

  // Rating address
  const ratingAddressDiff = diff(
    oldPolicy.rating_address,
    newPolicy.rating_address
  ) as IAddress;
  if (!isEmpty(ratingAddressDiff)) {
    policyDiff.rating_address = ratingAddressDiff;
  }

  // Details
  const detailsDiff = diff(
    oldPolicy.details,
    newPolicy.details
  ) as IAutoPolicyDetails;
  if (!isEmpty(detailsDiff)) {
    policyDiff.details = detailsDiff;
    if (!isEmpty(detailsDiff.drivers)) {
      // @ts-ignore delete
      delete policyDiff.details.drivers;
    }
    if (!isEmpty(detailsDiff.vehicles)) {
      // @ts-ignore delete
      delete policyDiff.details.vehicles;
    }
  }

  // Payment method
  if (oldPolicy.payment_method.id !== newPolicy.payment_method.id) {
    policyDiff.payment_method = { id: newPolicy.payment_method.id };
  }

  // Policy holders
  each(newPolicy.policy_holders, (updatedPolicyHolder) => {
    if (!updatedPolicyHolder.id) {
      policyDiff.policy_holders = policyDiff.policy_holders || [];
      policyDiff.policy_holders.push(updatedPolicyHolder);
    } else {
      const oldPolicyHolder =
        find(oldPolicy.policy_holders, {
          id: updatedPolicyHolder.id,
        }) || {};
      const policyHolderDiff = diff(
        oldPolicyHolder,
        updatedPolicyHolder
      ) as IAutoPolicyHolder;
      if (!isEmpty(policyHolderDiff)) {
        policyDiff.policy_holders = policyDiff.policy_holders || [];
        policyDiff.policy_holders.push({
          ...policyHolderDiff,
          id: updatedPolicyHolder.id,
        });
      }
    }
  });

  // Drivers
  each(get(newPolicy, 'details.drivers', []), (driver) => {
    if (!driver.id) {
      policyDiff.details = policyDiff.details || ({} as IAutoPolicyDetails);
      policyDiff.details.drivers = policyDiff.details.drivers || [];
      policyDiff.details.drivers.push(driver);
    } else {
      const oldDriver = find(get(oldPolicy, 'details.drivers'), {
        id: driver.id,
      });
      const driverDiff = diff(oldDriver, driver) as IPolicyDriver;

      if (!isEmpty(driverDiff) && !get(oldDriver, 'deleted')) {
        policyDiff.details!.drivers = policyDiff.details?.drivers || [];

        const updatedDriver = {
          id: driver.id,
          ...(driverDiff.deleted
            ? { deleted: driverDiff.deleted }
            : driverDiff),
        } as IPolicyDriver;

        policyDiff.details!.drivers.push(updatedDriver);
      }
    }
  });

  // Vehicles
  each(get(newPolicy, 'details.vehicles', []), (updatedVehicle) => {
    if (!updatedVehicle.id) {
      policyDiff.details = policyDiff.details || ({} as IAutoPolicyDetails);
      policyDiff.details.vehicles = policyDiff.details.vehicles || [];
      policyDiff.details.vehicles.push(updatedVehicle);
    } else {
      const updatedVehicleNoAddress = omit(updatedVehicle, 'garage_address');
      const oldVehicleNoAddress = omit(
        find(get(oldPolicy, 'details.vehicles'), {
          id: updatedVehicle.id,
        }),
        'garage_address'
      );
      const vehicleDiff = diff(
        oldVehicleNoAddress,
        updatedVehicleNoAddress
      ) as IPolicyVehicle;
      if (!isEmpty(vehicleDiff) && !get(oldVehicleNoAddress, 'deleted')) {
        policyDiff.details!.vehicles = policyDiff.details?.vehicles || [];
        policyDiff.details!.vehicles.push({
          id: updatedVehicle.id,
          ...(vehicleDiff.deleted
            ? { deleted: vehicleDiff.deleted }
            : vehicleDiff),
        } as IPolicyVehicle);
      }
    }
  });

  return policyDiff;
}
